
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { 
  Building2, 
  Zap, 
  TrendingUp, 
  Globe, 
  Plus, 
  Cpu,
  Layers,
  Unlock,
  Lock,
  Search,
  Settings,
  Terminal,
  Server,
  MoreHorizontal,
  ExternalLink,
  MapPin,
  Phone,
  Mail,
  RefreshCw
} from 'lucide-react';
import { Company, UserRole, Plan } from './types';
import { Card, Badge } from './SharedComponents';

interface SuperAdminDashboardProps {
  companies: Company[];
  onNewCompany: () => void;
  onNavigate: (tab: string) => void;
  onToggleBlock: (id: string) => void;
  onRefresh?: () => void;
}

export const SuperAdminDashboard = ({ companies, onNewCompany, onNavigate, onToggleBlock, onRefresh }: SuperAdminDashboardProps) => {
  const [search, setSearch] = useState('');
  const [activeMenu, setActiveMenu] = useState<{id: string, x: number, y: number} | null>(null);

  const globalStats = useMemo(() => {
    const totalMrr = companies.reduce((acc, c) => acc + (c.mrr || 0), 0);
    const activeCompanies = companies.filter(c => c.status === 'ACTIVE').length;
    const totalIaRequests = companies.reduce((acc, c) => acc + (c.iaUsageCount || 0), 0);
    const totalEquity = companies.reduce((acc, c) => acc + (c.equityValue || 0), 0);

    return { totalMrr, activeCompanies, totalIaRequests, totalEquity };
  }, [companies]);

  const filtered = companies.filter(c => 
    (c.name || '').toLowerCase().includes(search.toLowerCase()) || 
    (c.cnpj || '').includes(search)
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-20 bg-[#f8f9fa] min-h-screen text-secondary">
      {/* Header Estilo Terminal Master */}
      <div className="bg-[#0f172a] text-white p-10 rounded-b-[60px] shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-5"><Terminal size={300} /></div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div>
            <div className="flex items-center gap-2 text-primary font-black uppercase text-[10px] tracking-[0.4em] mb-4">
               <div className="w-2 h-2 bg-primary rounded-full animate-ping"></div>
               Infraestructure Master Node
            </div>
            <h1 className="text-5xl font-black uppercase tracking-tighter leading-none italic">Global <span className="text-primary not-italic">Controller</span></h1>
            <p className="text-white/40 font-bold uppercase tracking-widest text-[11px] mt-4 flex items-center gap-2">
               <Server size={14} /> Sorocaba Node-01 (Primary Cluster)
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={onRefresh || (() => window.location.reload())}
              className="bg-white/10 text-white p-6 rounded-[24px] font-black uppercase text-xs tracking-widest flex items-center gap-3 hover:bg-white/20 transition-all active:scale-95"
              title="Recarregar Dados"
            >
              <RefreshCw size={20} />
            </button>
            <button 
              onClick={onNewCompany}
              className="bg-primary text-white px-10 py-6 rounded-[24px] font-black uppercase text-xs tracking-widest flex items-center gap-3 hover:brightness-110 transition-all shadow-2xl shadow-primary/30 active:scale-95 group"
            >
              <Plus size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform" /> Provisionar Unidade
            </button>
          </div>
        </div>
      </div>

      <div className="px-10 -mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatItem icon={TrendingUp} label="MRR Global" value={`R$ ${globalStats.totalMrr.toLocaleString('pt-BR')}`} color="text-primary" />
        <StatItem icon={Globe} label="Unidades Ativas" value={globalStats.activeCompanies} color="text-emerald-500" />
        <StatItem icon={Cpu} label="IA Global Requests" value={globalStats.totalIaRequests.toLocaleString('pt-BR')} color="text-blue-500" />
        <StatItem icon={Layers} label="Patrimônio em Campo" value={`R$ ${(globalStats.totalEquity / 1000000).toFixed(1)}M`} color="text-amber-500" />
      </div>

      <div className="px-10 grid grid-cols-1 xl:grid-cols-3 gap-8 mt-8">
        <Card className="xl:col-span-2 p-8 bg-white border-none shadow-xl rounded-[40px] overflow-visible">
          <div className="flex flex-col md:flex-row justify-between items-center mb-10 px-2 gap-4">
            <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-3 italic">
              <Building2 size={24} className="text-primary" /> Ecossistema de Tenants
            </h3>
            <div className="relative group w-full md:w-auto">
               <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
               <input 
                className="pl-12 pr-6 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold text-sm w-full md:w-64 focus:ring-4 focus:ring-primary/5 transition-all" 
                placeholder="Pesquisar empresa..." 
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          
          <div className="overflow-x-auto scrollbar-hide overflow-visible">
            <table className="w-full text-left border-separate border-spacing-y-4 min-w-[900px]">
              <thead>
                <tr className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                  <th className="px-6 pb-2">Tenant / Localização / Contato</th>
                  <th className="pb-2 text-center">Status</th>
                  <th className="pb-2 text-center">Plano</th>
                  <th className="pb-2 text-right">Faturamento</th>
                  <th className="px-6 pb-2 text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="group hover:bg-primary/[0.02] transition-all relative overflow-visible">
                    <td className="py-6 px-6 rounded-l-[32px] bg-white shadow-sm border-l-4 border-l-primary">
                       <div className="flex items-center gap-5">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-2xl shadow-inner shrink-0 ${c.blocked ? 'bg-red-50 text-red-400' : 'bg-primary/5 text-primary'}`}>
                             {c.name.charAt(0)}
                          </div>
                          <div className="min-w-0">
                             <p className="font-black text-secondary uppercase text-sm leading-none mb-1 truncate">{c.name}</p>
                             <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight flex items-center gap-1 truncate mb-2">
                                <MapPin size={10} className="text-primary" /> {c.city} - {c.state} • {c.neighborhood}
                             </p>
                             <div className="flex items-center gap-4 opacity-70">
                                <span className="text-[9px] font-black text-green-600 flex items-center gap-1 uppercase tracking-widest">
                                   <Phone size={8} /> {c.whatsapp}
                                </span>
                                <span className="text-[9px] font-black text-gray-400 flex items-center gap-1 uppercase tracking-widest truncate max-w-[120px]">
                                   <Mail size={8} /> {c.contactEmail}
                                </span>
                             </div>
                          </div>
                       </div>
                    </td>
                    <td className="py-6 bg-white shadow-sm text-center">
                       <Badge className={c.blocked ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-600 border-green-100'}>
                         {c.blocked ? 'BLOQUEADO' : 'ATIVO'}
                       </Badge>
                    </td>
                    <td className="py-6 bg-white shadow-sm text-center">
                       <Badge className="bg-secondary text-white border-none text-[8px] px-2">{c.plan}</Badge>
                    </td>
                    <td className="py-6 bg-white shadow-sm text-right font-black text-secondary">
                       R$ {c.mrr.toLocaleString('pt-BR')}
                    </td>
                    <td className="py-6 px-6 rounded-r-[32px] bg-white shadow-sm text-right overflow-visible">
                       <div className="relative inline-block">
                          <button 
                            onClick={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setActiveMenu(activeMenu?.id === c.id ? null : { id: c.id, x: rect.left - 240, y: rect.bottom + 10 });
                            }}
                            className={`p-3 rounded-xl transition-all shadow-sm ${activeMenu?.id === c.id ? 'bg-primary text-white' : 'bg-gray-50 text-gray-300 hover:text-primary hover:bg-white border border-transparent hover:border-primary/20'}`}
                          >
                             <MoreHorizontal size={20} />
                          </button>
                          
                          {activeMenu?.id === c.id && (
                            <>
                              <div className="fixed inset-0 z-[1000]" onClick={() => setActiveMenu(null)}></div>
                              <div 
                                className="fixed w-64 bg-white border border-gray-100 rounded-3xl shadow-2xl z-[2000] overflow-hidden animate-in fade-in zoom-in-95 p-2 text-left"
                                style={{ top: activeMenu.y, left: activeMenu.x }}
                              >
                                 <button 
                                   onClick={() => { onNavigate('companies'); setActiveMenu(null); }}
                                   className="w-full text-left px-4 py-3 text-[10px] font-black uppercase text-secondary hover:bg-primary/5 hover:text-primary rounded-2xl transition-all flex items-center gap-3"
                                 >
                                    <Settings size={16} /> Gerenciar Unidade
                                 </button>
                                 <button 
                                   onClick={() => { onToggleBlock(c.id); setActiveMenu(null); }}
                                   className={`w-full text-left px-4 py-3 text-[10px] font-black uppercase rounded-2xl transition-all flex items-center gap-3 ${c.blocked ? 'text-green-600 hover:bg-green-50' : 'text-red-500 hover:bg-red-50'}`}
                                 >
                                    {c.blocked ? <Unlock size={16} /> : <Lock size={16} />}
                                    {c.blocked ? 'Liberar Acesso' : 'Bloquear Acesso'}
                                 </button>
                                 <div className="h-[1px] bg-gray-50 mx-2 my-1"></div>
                                 <button 
                                   className="w-full text-left px-4 py-3 text-[10px] font-black uppercase text-gray-400 hover:bg-gray-50 rounded-2xl transition-all flex items-center gap-3"
                                 >
                                    <ExternalLink size={16} /> Ver Dashboard Unidade
                                 </button>
                              </div>
                            </>
                          )}
                       </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="space-y-6">
           <Card className="p-8 bg-secondary text-white border-none shadow-2xl rounded-[40px] relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform"><Zap size={80} /></div>
              <h4 className="text-xl font-black uppercase italic tracking-tight mb-8">Node Stats</h4>
              <div className="space-y-6">
                 <KPI label="Uptime Plataforma" value="99.98%" />
                 <KPI label="Queue Workers" value="Ativos" status="bg-green-500" />
                 <KPI label="DB Replication" value="Synced" status="bg-green-500" />
              </div>
           </Card>

           <Card className="p-8 bg-white border border-gray-100 shadow-xl rounded-[40px]">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-6">Alertas de Pagamento</h4>
              <div className="space-y-4">
                 {companies.filter(c => c.status === 'PAST_DUE').map(c => (
                   <div key={c.id} className="flex justify-between items-center p-4 bg-red-50 rounded-2xl border border-red-100">
                      <span className="text-[10px] font-black uppercase text-red-700">{c.name}</span>
                      <Badge className="bg-red-500 text-white border-none text-[8px]">- R$ {c.payable}</Badge>
                   </div>
                 ))}
                 {companies.filter(c => c.status === 'PAST_DUE').length === 0 && (
                   <div className="py-10 text-center opacity-20 italic font-bold text-xs uppercase">Sem pendências críticas</div>
                 )}
              </div>
           </Card>
        </div>
      </div>
    </div>
  );
};

const StatItem = ({ icon: Icon, label, value, color }: any) => (
  <Card className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-lg flex flex-col gap-4 group hover:scale-[1.03] transition-all cursor-default">
    <div className={`w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center transition-all group-hover:bg-primary group-hover:text-white ${color}`}>
       <Icon size={28} />
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-3xl font-black text-secondary tracking-tighter">{value}</p>
    </div>
  </Card>
);

const KPI = ({ label, value, status }: any) => (
  <div className="flex justify-between items-center border-b border-white/5 pb-4 last:border-none">
     <span className="text-[9px] font-black uppercase text-white/40 tracking-widest">{label}</span>
     <div className="flex items-center gap-2">
        {status && <div className={`w-1.5 h-1.5 rounded-full ${status} animate-pulse`}></div>}
        <span className="text-sm font-black">{value}</span>
     </div>
  </div>
);
